// FavoritesAlerts.jsx

import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Row, Col, Tag, Tooltip, Checkbox, Button, Space, Card, Input, Select, Pagination } from 'antd';
import api from '../functions/api';
import getUserId from '../functions/getUserId';
import ThreeTablesModal from "./SearchResultModal";
import MarketAlerts from "./MarketAlerts"; // Import MarketAlerts component
import { renderAlertEffectiveness } from '../functions/alertClauseService'; // Import renderAlertEffectiveness
import InteractiveAlertCard from './InteractiveAlertCard';
import InteractiveCompactAlertCard from './InteractiveCompactAlertCard';
import { compareByUrgency } from '../utils/urgency'; // DAS-2532

const { Search } = Input;

// Redux action to update the 'seen' status
const updateSeenStatus = (id, seen) => {
  return {
    type: 'UPDATE_SEEN_STATUS',
    payload: { id, seen },
  };
};

// Redux action to update the 'followed_up' status
export const updateFollowedUpStatus = (id, followed_up) => ({
  type: 'UPDATE_FOLLOWED_UP_STATUS',
  payload: { id, followed_up },
});

const FavoritesAlerts = () => {
  const dispatch = useDispatch();
  // Fetch the alerts data from the Redux store
  const rawAlerts = useSelector(state => state.favorites?.data?.favorites_alert_data) || [];
  // console.log('rawAlerts', rawAlerts);
  const companyData = useSelector(state => state.favorites?.data?.data) || []; // Get company info
  
  // Pagination and filtering state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [searchText, setSearchText] = useState('');
  const [selectedAlertType, setSelectedAlertType] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('default');

  // Create a dictionary for fast lookup: { Ticker: Company Name }
  const companyMap = companyData.reduce((acc, item) => {
    if (item.Ticker) {
      acc[item.Ticker] = item.Company || 'N/A';
    }
    return acc;
  }, {});

  // Merge company names into alerts
  const alerts = rawAlerts
  .filter(alert => alert.bignore !== 1) // ⬅️ Filter out ignored alerts
  .map(alert => ({
    ...alert,
    company: companyMap[alert.ticker] || 'N/A',
  }));

  const getAlertAge = (alert) => {
    if (!alert.epoch_initiation) return 0;
    return Math.floor((Date.now() / 1000 - alert.epoch_initiation) / 86400);
  };

  const sortedAlerts = Array.isArray(alerts)
  ? [...alerts].sort((a, b) => {
      // Always: unseen first
      const seenA = a.seen === null ? 0 : Number(a.seen);
      const seenB = b.seen === null ? 0 : Number(b.seen);
      if (seenA !== seenB) return seenA - seenB;

      // User-selected sort
      switch (sortBy) {
        case 'alert_type':
          return (a.alert_type || '').localeCompare(b.alert_type || '');
        case 'age_desc':
          return getAlertAge(b) - getAlertAge(a);
        case 'age_asc':
          return getAlertAge(a) - getAlertAge(b);
        case 'alignment_desc':
          return (b.LLM_analysis_alignment_score ?? 0) - (a.LLM_analysis_alignment_score ?? 0);
        case 'fp_risk_asc':
          return (a.LLM_false_positive_score ?? 10) - (b.LLM_false_positive_score ?? 10);
        case 'confirmation_desc':
          return (parseInt(b.pf_weighted_confirmation_percentage, 10) || 0) - (parseInt(a.pf_weighted_confirmation_percentage, 10) || 0);
        case 'confidence_desc':
          return (b.pf_final_confidence_score ?? 0) - (a.pf_final_confidence_score ?? 0);
        case 'opportunity_desc':
          return (b.opportunity_factor ?? 0) - (a.opportunity_factor ?? 0);
        case 'ticker_asc':
          return (a.ticker || '').localeCompare(b.ticker || '');
        case 'company_asc':
          return (a.company || '').localeCompare(b.company || '');
        case 'urgency_desc':
        default:
          // DAS-2532: urgency is the default sort (high -> low; ties: confidence, then ticker).
          return compareByUrgency(a, b);
      }
    })
  : [];

  // Filter data based on search and filters
  const filteredData = sortedAlerts.filter(alert => {
    const matchesSearch = !searchText || 
      alert.company?.toLowerCase().includes(searchText.toLowerCase()) ||
      alert.ticker?.toLowerCase().includes(searchText.toLowerCase()) ||
      alert.pf_alert_text?.toLowerCase().includes(searchText.toLowerCase());
    
    const matchesAlertType = selectedAlertType === 'all' || alert.alert_type === selectedAlertType;
    const matchesCategory = selectedCategory === 'all' || alert.Category === selectedCategory;
    
    return matchesSearch && matchesAlertType && matchesCategory;
  });

  // Get current page data
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentData = filteredData.slice(startIndex, endIndex);

  const alertEffectiveness = useSelector(state => state?.portfolio?.data?.alert_effectiveness_data);

  const userId = getUserId();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [isMarketAlertsVisible, setIsMarketAlertsVisible] = useState(false);
  const [selectedAlertSubtypeIdentifier, setSelectedAlertSubtypeIdentifier] = useState(null);

  const handleRowClick = (ticker) => {
    setSelectedTicker(ticker);
    setIsModalVisible(true);
  };

  const handleCheckboxChange = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      await api.post('/pf_alert_check_update', { id, seen: newStatus });

      dispatch({
        type: 'UPDATE_SEEN_STATUS',
        payload: { id, seen: newStatus },
      });

      // Force re-fetch alerts from Redux store to trigger re-render
      dispatch({ type: 'REFRESH_ALERTS' });

    } catch (error) {
      console.error('Failed to update seen status', error);
    }
  };

  const handleFollowedUpChange = async (id, seen, followed_up) => {
    try {
      const newFollowedUp = followed_up === 1 ? 0 : 1;
      await api.post('/pf_alert_check_update', { id, followed_up: newFollowedUp });

      dispatch(updateFollowedUpStatus(id, newFollowedUp));

      // Force re-fetch alerts from Redux store to trigger re-render
      dispatch({ type: 'REFRESH_ALERTS' });

    } catch (error) {
      console.error('Failed to update followed up status', error);
    }
  };

  const handleOpenMarketAlerts = (alertSubtypeIdentifier) => {
    setSelectedAlertSubtypeIdentifier(alertSubtypeIdentifier);
    setIsMarketAlertsVisible(true);
  };

  const handleCloseMarketAlerts = () => {
    setIsMarketAlertsVisible(false);
    setSelectedAlertSubtypeIdentifier(null);
  };

  // Get unique alert types and categories for filters
  const alertTypes = [...new Set(sortedAlerts.map(alert => alert.alert_type))];
  const categories = [...new Set(sortedAlerts.map(alert => alert.Category))];

  return (
    <> 
      <div style={{ padding: '20px' }}>
        <h2>Favorites Alerts</h2>
        
        {/* Filters and Search */}
        <Space style={{ marginBottom: '20px', width: '100%' }} direction="vertical" size="middle">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8} lg={6}>
              <Search
                placeholder="Search by company, ticker, or alert text..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Filter by Alert Type"
                value={selectedAlertType}
                onChange={setSelectedAlertType}
                style={{ width: '100%' }}
              >
                <Select.Option value="all">All Alert Types</Select.Option>
                {alertTypes.map(type => (
                  <Select.Option key={type} value={type}>{type}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Filter by Category"
                value={selectedCategory}
                onChange={setSelectedCategory}
                style={{ width: '100%' }}
              >
                <Select.Option value="all">All Categories</Select.Option>
                {categories.map(category => (
                  <Select.Option key={category} value={category}>{category}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                value={sortBy}
                onChange={(val) => { setSortBy(val); setCurrentPage(1); }}
                style={{ width: '100%' }}
              >
                <Select.Option value="default">Sort: Urgency (default)</Select.Option>
                <Select.Option value="alert_type">Sort: Alert Type</Select.Option>
                <Select.Option value="age_desc">Sort: Age (oldest first)</Select.Option>
                <Select.Option value="age_asc">Sort: Age (newest first)</Select.Option>
                <Select.Option value="alignment_desc">Sort: Alignment (high→low)</Select.Option>
                <Select.Option value="fp_risk_asc">Sort: FP Risk (low→high)</Select.Option>
                <Select.Option value="confirmation_desc">Sort: Confirmation (high→low)</Select.Option>
                <Select.Option value="confidence_desc">Sort: Weighted Confidence (high→low)</Select.Option>
                <Select.Option value="opportunity_desc">Sort: Opportunity (high→low)</Select.Option>
                <Select.Option value="ticker_asc">Sort: Ticker (A→Z)</Select.Option>
                <Select.Option value="company_asc">Sort: Company (A→Z)</Select.Option>
              </Select>
            </Col>
          </Row>
          <Row>
            <Col xs={24}>
              <div style={{ textAlign: 'right' }}>
                <span style={{ color: '#666' }}>
                  Showing {currentData.length} of {filteredData.length} alerts
                </span>
              </div>
            </Col>
          </Row>
        </Space>

        {/* Cards Grid */}
        <Row gutter={[20, 20]}>
          {currentData.map((alert) => (
            <Col xs={24} sm={12} lg={8} xl={6} key={alert.ID}>
              <InteractiveCompactAlertCard
                alert={alert}
                alertEffectiveness={alertEffectiveness}
                onCheckboxChange={handleCheckboxChange}
                onFollowedUpChange={handleFollowedUpChange}
                onReinvestmentCheck={(id, reinvestment) => {
                  // TODO: implement reinvestment update API call here
                }}
                openFPDialog={() => {
                  // TODO: implement FP dialog opening here
                }}
              />
            </Col>
          ))}
        </Row>

        {/* Pagination */}
        {filteredData.length > pageSize && (
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <Pagination
              current={currentPage}
              total={filteredData.length}
              pageSize={pageSize}
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              }}
              showSizeChanger
              showQuickJumper
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} alerts`}
            />
          </div>
        )}
      </div>

      {isModalVisible && (
        <ThreeTablesModal 
          tickerValue={selectedTicker} 
          open={isModalVisible} 
          onClose={() => setIsModalVisible(false)} 
          userId={userId} 
        />
      )}
      {isMarketAlertsVisible && (
        <MarketAlerts
          isVisible={isMarketAlertsVisible}
          onClose={handleCloseMarketAlerts}
          alertSubtypeIdentifier={selectedAlertSubtypeIdentifier}
        />
      )}
    </>
  );
};

export default FavoritesAlerts;